import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useMemo } from 'react';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2 font-heading">文章未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">该文章可能已被移除或链接无效</p>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-bold text-foreground-900 mt-10 mb-4 font-heading">
            {line.replace('## ', '')}
          </h2>
        );
        i++;
        continue;
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-foreground-800 mt-8 mb-3 font-heading">
            {line.replace('### ', '')}
          </h3>
        );
        i++;
        continue;
      }

      if (line.startsWith('#### ')) {
        elements.push(
          <h4 key={i} className="text-base font-semibold text-foreground-700 mt-6 mb-2 font-heading">
            <a id={`section-${i}`}>{line.replace('#### ', '')}</a>
          </h4>
        );
        i++;
        continue;
      }

      if (line.startsWith('- ')) {
        const listItems: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('- ')) {
          listItems.push(lines[i].trim().replace('- ', ''));
          i++;
        }
        elements.push(
          <ul key={i} className="space-y-2 my-3 ml-4">
            {listItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        );
        continue;
      }

      if (line === '') {
        i++;
        continue;
      }

      elements.push(
        <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-4">
          {line}
        </p>
      );
      i++;
    }

    return elements;
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.image,
            url: `https://mylifeguide.github.io/article/${article.id}`,
            inLanguage: 'zh-CN',
            articleSection: article.category,
          }),
        }}
      />

      <article className="min-h-screen bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.category, to: `/categories?cat=${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 leading-snug mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-3">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                <div className="text-xs text-foreground-400">作者</div>
              </div>
            </div>
          </div>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>

          <div className="prose-custom">{renderContent(article.content)}</div>

          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 text-xs border border-background-200/70"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/70">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}